/* eslint-disable react-hooks/exhaustive-deps */
import {observer} from "mobx-react-lite";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  useMediaQuery,
  useToast,
  VStack
} from "@chakra-ui/react";
import React, {useCallback, useEffect, useState} from "react";
import {useFormik} from "formik";
import * as yup from "yup";
import {FiGlobe, FiLock} from "react-icons/fi";
import {useUIManager} from "../hooks/useUIManager";
import {useAPI} from "../hooks/useAPI";
import {BookingVisibility, ListVisibility} from "../services/responses";
import {toastMessage} from "../services/notify";
import {BookingVisibilitySelect} from "./BookingVisibilitySelect";

const VISIBILITIES: { value: ListVisibility, label: string, hint: string, icon?: typeof FiLock }[] = [
  {value: 'public', label: 'Public', hint: 'Shown on your profile — anyone can open it.'},
  {value: 'private', label: 'Private', hint: 'Only you can see and open it.', icon: FiLock},
  {value: 'link', label: 'By link', hint: 'Only you see it on the profile, but anyone with the link can open it.', icon: FiGlobe},
];

const ListModal = () => {
  const api = useAPI();
  const uiManager = useUIManager();
  const toast = useToast();
  const [isLg] = useMediaQuery("(min-width: 992px)");

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showConfirmation, toggleConfirmation] = useState(false);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const isEdit = uiManager.editListId !== null;

  const submit = ({name, description, visibility, bookingVisibility}: {
    name: string,
    description: string,
    visibility: ListVisibility,
    bookingVisibility: BookingVisibility,
  }) => {
    if (loading) {
      return;
    }

    setLoading(true);
    const request = isEdit
      ? api.patchList(uiManager.editListId as string, name, description || null, visibility, bookingVisibility)
      : api.createList(name, description || null, visibility, bookingVisibility);
    request
      .then((response) => {
        if (response === null) {
          toast(toastMessage('Unexpected server error', 'error'));
          return;
        }
        uiManager.toggleNeedReloadLists(true);
        uiManager.toggleListModal(false);
      })
      .finally(() => setLoading(false));
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      visibility: 'public' as ListVisibility,
      bookingVisibility: 'owner' as BookingVisibility,
    },
    onSubmit: submit,
    validationSchema: yup.object().shape({
      name: yup.string().required().min(1).max(64),
      description: yup.string().nullable().min(1).max(255),
    }),
  });

  const resetForm = useCallback(() => {
    formik.setFieldValue('name', '');
    formik.setFieldValue('description', '');
    formik.setFieldValue('visibility', 'public');
    formik.setFieldValue('bookingVisibility', 'owner');
  }, []);

  useEffect(() => {
    if (!uiManager.listModalShown) {
      return;
    }

    if (!isEdit) {
      resetForm();
      setInitialized(true);
      return;
    }

    setInitialized(false);
    setLoading(true);
    api.getList(uiManager.editListId as string)
      .then((list) => {
        if (list === null) {
          uiManager.toggleListModal(false);
          return;
        }
        formik.setFieldValue('name', list.name);
        formik.setFieldValue('description', list.description || '');
        formik.setFieldValue('visibility', list.visibility);
        formik.setFieldValue('bookingVisibility', list.booking_visibility);
        setInitialized(true);
      })
      .finally(() => setLoading(false));
  }, [uiManager.listModalShown, uiManager.editListId]);

  const onClose = () => {
    resetForm();
    uiManager.toggleListModal(false);
  };

  const deleteList = () => {
    if (!isEdit) {
      return;
    }

    setLoading(true);
    api.deleteList(uiManager.editListId as string)
      .then((response) => {
        if (response === null) {
          toast(toastMessage('Cannot delete the list due to a server error', 'error'));
          return;
        }
        toggleConfirmation(false);
        uiManager.toggleNeedReloadLists(true);
        uiManager.toggleListModal(false);
      })
      .finally(() => setLoading(false));
  };

  return <>
    <Modal isCentered isOpen={uiManager.listModalShown} onClose={onClose} size={isLg ? '2xl' : 'full'}>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)"/>
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit list' : 'Create a list'}</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <Box mb={3}>
            <form id="list-form" onSubmit={formik.handleSubmit}>
              <FormControl mb={3} isInvalid={!!formik.errors.name && formik.touched.name}>
                <FormLabel>Name</FormLabel>
                <Input id="name" name="name" type="text" placeholder="Birthday, New Year, ..."
                       value={formik.values.name} onChange={formik.handleChange} readOnly={loading}/>
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl mb={3} isInvalid={!!formik.errors.description && formik.touched.description}>
                <FormLabel>Description (optional)</FormLabel>
                <Textarea id="description" name="description" placeholder="What is this list about?"
                          value={formik.values.description} onChange={formik.handleChange} readOnly={loading}/>
                <FormErrorMessage>{formik.errors.description}</FormErrorMessage>
              </FormControl>

              <FormControl mb={3}>
                <FormLabel>Visibility</FormLabel>
                <RadioGroup value={formik.values.visibility}
                            onChange={(value) => formik.setFieldValue('visibility', value)}>
                  <VStack align="stretch" spacing={2}>
                    {VISIBILITIES.map(({value, label, hint, icon}) => (
                      <Radio key={value} value={value} isDisabled={loading} colorScheme="brand"
                             alignItems="flex-start"
                             sx={{'.chakra-radio__control': {marginTop: '3px'}}}>
                        <Box>
                          <HStack spacing={2}>
                            {icon && <Icon as={icon} flexShrink={0}/>}
                            <Text fontWeight="medium" whiteSpace="nowrap">{label}</Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.500">{hint}</Text>
                        </Box>
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Booking mode</FormLabel>
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Guests can always book items in a list — choose who sees the bookings.
                </Text>
                <BookingVisibilitySelect
                  value={formik.values.bookingVisibility}
                  onChange={(value) => formik.setFieldValue('bookingVisibility', value)}
                  isDisabled={loading}
                />
              </FormControl>
            </form>
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          {isEdit
            ? <Button variant="outline" colorScheme="red" isDisabled={loading}
                      onClick={() => toggleConfirmation(true)}>Delete</Button>
            : <Box/>}
          <Flex>
            <Button variant="ghost" mr={2} onClick={onClose} isDisabled={loading}>Cancel</Button>
            <Button type="submit" form="list-form" colorScheme="brand" isDisabled={loading || !initialized}>
              {loading ? 'Please wait...' : (isEdit ? 'Save changes' : 'Create')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <AlertDialog motionPreset="slideInBottom" onClose={() => toggleConfirmation(false)}
                 isOpen={showConfirmation} isCentered leastDestructiveRef={cancelRef}>
      <AlertDialogOverlay/>
      <AlertDialogContent>
        <AlertDialogHeader>Delete list?</AlertDialogHeader>
        <AlertDialogCloseButton/>
        <AlertDialogBody>
          Are you sure you want to delete this list? All items in it will be removed. This action cannot be undone.
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button ref={cancelRef} onClick={() => toggleConfirmation(false)}>Don't delete</Button>
          <Button colorScheme="red" ml={3} onClick={deleteList} isDisabled={loading}>Delete</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
};

export default observer(ListModal);
